import React, { useState, useEffect, useRef, useCallback } from "react";
import * as Mui from "@mui/material";
import { useFetch } from "services/hooks/useFetch";
import {
  themes,
  scrollTop,
  stickyHeader,
  tableRowSize,
  StatusBadge,
  displayFlexItem,
  getStatusFlow,
} from "services/constants";
import { useSelector } from "react-redux";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import { Loader } from "components/Loader";
import { OTPostApprovalHeader } from "pages/TeamZone/TeamZoneHeaders";
import { Typography, Box, Paper } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { EditCheckInOut } from "pages/TeamZone/Attendance/AttendanceStatus/Status/EditCheckInOut";
import { ArrowRight } from "@mui/icons-material";
import { StatusRow } from "components/CustomComponents/StatusRow";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { showToast } from "services/utils/Status";

export const OTPostApproval = () => {
  const { domain, userScreenAccess, topbarCompanyId, userDetails } =
    useSelector((state) => state.tokenReducer);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [otPostApprovalDetails, setOtPostApprovalDetails] = useState([]);
  const containerRef = useRef(null);
  const [departmentId, setDepartmentId] = useState("");
  const [handleLoader, setHandleLoader] = useState(false);
  const [shiftDetails, setShiftDetails] = useState({});
  const [openSidePeek, setOpenSidePeek] = useState(false);
  const [editState, setEditState] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [expandedData, setExpandedData] = useState("");
  const {
    result: OTPostApprovalData,
    setLoad: reCallApi,
    loading,
  } = useFetch(
    `${domain}Team_Zone/Approve/Over_time/ot-post-approve/?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`
  );
  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Approve",
    "OT Post-Approval"
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setOtPostApprovalDetails(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage]
  );

  useEffect(() => {
    if (topbarCompanyId) {
      reCallApi(true);
      setHandleLoader(false);
    }
  }, [reCallApi, topbarCompanyId]);

  useEffect(() => {
    const recallApi = () => {
      setEditState(false);
      reCallApi(true);
    };
    if (editState && !openSidePeek) {
      recallApi();
    }
  });

  const handleToggle = (index, value) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
    setExpandedData(value);
  };

  return !handleLoader && loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Overtime Post-Approval
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track and history of overtime payroll requests that are yet to be
            processed or approved for payment.
          </Typography>
        </Box>
        <Paper sx={{ width: "100%", height: "78vh" }}>
          <CustomFilter
            title={"Overtime Post-Approval Records"}
            filterHeaders={OTPostApprovalHeader}
            data={OTPostApprovalData}
            filteredData={otPostApprovalDetails}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
          />

          <Mui.Grid sx={{ ml: 2, mr: 2 }}>
            <Mui.TableContainer ref={containerRef} sx={{ maxHeight: 560 }}>
              <Mui.Table className="border">
                <Mui.TableHead sx={stickyHeader}>
                  <Mui.TableRow sx={tableRowSize}>
                    {OTPostApprovalHeader?.map((value) => (
                      <Mui.TableCell
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                        }}
                      >
                        {value?.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {otPostApprovalDetails?.length === 0 ? (
                    <InfoMessage dataLength={OTPostApprovalData} />
                  ) : (
                    otPostApprovalDetails
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      ?.map((value, index) => (
                        <React.Fragment key={index}>
                          <Mui.TableRow sx={tableRowSize}>
                            {OTPostApprovalHeader.map((head) => (
                              <Mui.TableCell
                                key={head.key}
                                onClick={() => {
                                  if (check_access) {
                                    if (head.key === "expand") {
                                      handleToggle(index, value);
                                    } else {
                                      setShiftDetails({
                                        user_id: value?.user_id,
                                        shift_filter_date: value?.date
                                          .split("-")
                                          .reverse()
                                          .join("-"),
                                        date: value?.date,
                                        user_name: value?.employee_name,
                                        shift_name: value?.shift,
                                        latest_pay_date: value?.last_pay_date,
                                        type: value?.status,
                                      });
                                      setOpenSidePeek(true);
                                      setDepartmentId(value?.department_id);
                                      setExpandedIndex(null);
                                    }
                                  } else {
                                    showToast(
                                      "error",
                                      "Access denied: You are not authorized to modify overtime post approval"
                                    );
                                  }
                                }}
                                sx={{
                                  width:
                                    head.key === "expand" ? "20px" : "auto",
                                }}
                              >
                                {head.key === "expand" ? (
                                  <ArrowRight
                                    sx={{
                                      transition: "0.3s",
                                      transform:
                                        expandedIndex === index
                                          ? "rotate(90deg)"
                                          : "rotate(0deg)",
                                      cusror: "pointer",
                                    }}
                                  />
                                ) : head.key === "employee_name" ? (
                                  <Mui.Stack direction="Row">
                                    <Mui.Avatar
                                      sx={{ width: 23, height: 23 }}
                                      alt={value?.employee_name
                                        ?.charAt(0)
                                        .toUpperCase()}
                                      src={value?.image_url}
                                    />
                                    &nbsp;
                                    {value?.employee_name}
                                  </Mui.Stack>
                                ) : head.key === "status" ? (
                                  <Mui.Box sx={displayFlexItem}>
                                    <StatusBadge
                                      color={value?.color_code}
                                      label={value?.status || "N/A"}
                                    />
                                  </Mui.Box>
                                ) : (
                                  value[head.key] ?? "N/A"
                                )}
                              </Mui.TableCell>
                            ))}
                          </Mui.TableRow>

                          {expandedIndex === index && (
                            <Mui.TableRow>
                              <Mui.TableCell
                                colSpan={OTPostApprovalHeader.length}
                                sx={{ p: 2 }}
                              >
                                {getStatusFlow(expandedData)[
                                  expandedData?.status
                                ]?.map((item, i, arr) => (
                                  <StatusRow
                                    key={i}
                                    status={item.status}
                                    user={item.user}
                                    date={item.date}
                                    showConnector={i !== arr.length - 1}
                                  />
                                ))}
                              </Mui.TableCell>
                            </Mui.TableRow>
                          )}
                        </React.Fragment>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            <Box sx={{ mt: 1 }}>
              {otPostApprovalDetails?.length > 25 && (
                <Mui.TablePagination
                  className="custom-pagination"
                  component="div"
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  count={otPostApprovalDetails?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
            </Box>
          </Mui.Grid>
        </Paper>
      </Box>
      {openSidePeek ? (
        <EditCheckInOut
          setOpenSidePeek={setOpenSidePeek}
          openSidePeek={openSidePeek}
          shiftDetails={shiftDetails}
          setEditState={setEditState}
          departmentId={departmentId}
        />
      ) : null}
    </React.Fragment>
  );
};
